import { parseNumericDraft } from '../../../shared/utils/numericInput';
import { entitySelectionPayload } from '../../../shared/utils/entitySelection';
import { parseEuroToCents } from '../../../shared/utils/installments';
import { getStoredSessionToken } from '../../../app/services/session.service';

const ACCOUNTING_API_BASE_URL = (process.env.REACT_APP_ACCOUNTING_API_BASE_URL || '').replace(
  /\/$/,
  ''
);
const EXPENSES_API_PATH = '/api/accounting/expenses';
const PAYMENTS_API_PATH = '/api/accounting/payments';
const INVOICES_API_PATH = '/api/accounting/invoices';

function buildMutationResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

function getAccountingApiUrl(path = '') {
  const resolvedPath = path || EXPENSES_API_PATH;
  return `${ACCOUNTING_API_BASE_URL}${resolvedPath}`;
}

function isAuthFailure(status) {
  return status === 401 || status === 403;
}

async function readErrorPayload(response) {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

async function isModuleDisabledResponse(response) {
  if (response.status !== 403) return false;

  const payload = await readErrorPayload(response);
  return payload?.error === 'module_disabled';
}

async function readCapabilityDeniedPayload(response) {
  if (response.status !== 403) return null;

  const payload = await readErrorPayload(response);
  return payload?.error === 'capability_denied' ? payload : null;
}

async function getTenantDatabaseFailure(response) {
  const payload = await readErrorPayload(response);
  const error = payload?.error;

  if (error === 'tenant_database_not_ready') {
    return {
      error: 'ACCOUNTING_TENANT_DATABASE_NOT_READY',
      message: payload?.message || 'Database tenant accounting non ancora pronto.',
    };
  }

  if (error === 'tenant_database_unavailable') {
    return {
      error: 'ACCOUNTING_TENANT_DATABASE_UNAVAILABLE',
      message: payload?.message || 'Database tenant accounting non disponibile.',
    };
  }

  if (error === 'tenant_database_metadata_missing') {
    return {
      error: 'ACCOUNTING_TENANT_DATABASE_METADATA_MISSING',
      message: payload?.message || 'Metadata runtime tenant accounting incompleti.',
    };
  }

  return null;
}

function getAccountingAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function mapApiExpenseToExpense(expense) {
  return {
    id: expense?.id,
    description: expense?.description || '',
    type: expense?.type || 'spesa_aziendale',
    amount:
      typeof expense?.amount === 'number'
        ? expense.amount
        : parseFloat(expense?.amount || 0),
    dueDate: expense?.dueDate || '',
    paymentDate: expense?.paymentDate || null,
    status: expense?.status || 'da_saldare',
    ...entitySelectionPayload(expense),
    ...(expense?.recurrenceCount != null ? { recurrenceCount: expense.recurrenceCount } : {}),
    ...(expense?.recurrenceNumber != null ? { recurrenceNumber: expense.recurrenceNumber } : {}),
    recurring: expense?.recurring || 'una_tantum',
    recurrenceParentId: expense?.recurrenceParentId ?? null,
    recurrenceFrequency: expense?.recurrenceFrequency || null,
    recurrenceGeneratedForDate: expense?.recurrenceGeneratedForDate || null,
    nextRecurrenceDueDate: expense?.nextRecurrenceDueDate || null,
    recipientType: expense?.recipientType || 'OTHER',
    staffId: expense?.staffId ?? null,
    supplierId: expense?.supplierId ?? null,
    supplierName: expense?.supplierName || '',
    notes: expense?.notes || '',
    customFields: expense?.customFields || {},
  };
}

function mapApiPaymentToPayment(payment) {
  return {
    ...entitySelectionPayload(payment),
    id: payment?.id,
    clientId: payment?.clientId ?? null,
    invoiceId: payment?.invoiceId ?? null,
    completedDealId: payment?.completedDealId ?? null,
    inProgressDealId: payment?.inProgressDealId ?? null,
    invoiceNumber: payment?.invoiceNumber || '',
    client: payment?.client || '',
    assignment: payment?.assignment || '',
    amount:
      typeof payment?.amount === 'number'
        ? payment.amount
        : parseFloat(payment?.amount || 0),
    issueDate: payment?.issueDate || '',
    dueDate: payment?.dueDate || '',
    paymentDate: payment?.paymentDate || null,
    status: payment?.status || 'previsto',
    type: payment?.type || 'unico',
    installment: payment?.installment || '',
    notes: payment?.notes || '',
    reason: payment?.reason || '',
  };
}

function mapApiInvoiceToInvoice(invoice) {
  return {
    ...entitySelectionPayload(invoice),
    id: invoice?.id,
    invoiceNumber: invoice?.invoiceNumber || '',
    client: invoice?.client || '',
    clientId: invoice?.clientId ?? null,
    completedDealId: invoice?.completedDealId ?? null,
    assignment: invoice?.assignment || '',
    issueDate: invoice?.issueDate || '',
    totalAmount:
      typeof invoice?.totalAmount === 'number'
        ? invoice.totalAmount
        : parseFloat(invoice?.totalAmount || 0),
    status: invoice?.status || 'emessa',
    customFields: invoice?.customFields || {},
  };
}

async function performListExpensesRequest() {
  const response = await fetch(getAccountingApiUrl(EXPENSES_API_PATH), {
    headers: getAccountingAuthHeaders(),
  });

  if (await isModuleDisabledResponse(response)) {
    throw new Error('ACCOUNTING_MODULE_DISABLED');
  }

  const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
  if (tenantDatabaseFailure) {
    const error = new Error(tenantDatabaseFailure.error);
    error.message = tenantDatabaseFailure.error;
    error.clientMessage = tenantDatabaseFailure.message;
    throw error;
  }

  if (!response.ok) {
    throw new Error(`ACCOUNTING_EXPENSES_LIST_FAILED_${response.status}`);
  }

  const payload = await response.json();
  if (!Array.isArray(payload)) {
    throw new Error('ACCOUNTING_EXPENSES_LIST_INVALID_PAYLOAD');
  }

  return payload.map(mapApiExpenseToExpense);
}

function attachClientIdsToPayments(payments, invoices) {
  const invoicesById = new Map(invoices.map((invoice) => [String(invoice.id), invoice]));
  const invoicesByNumber = new Map(invoices.filter((invoice) => invoice.invoiceNumber).map((invoice) => [invoice.invoiceNumber, invoice]));
  const invoiceClientIdsById = new Map(
    invoices
      .filter((invoice) => invoice?.id !== null && invoice?.id !== undefined)
      .map((invoice) => [String(invoice.id), invoice.clientId ?? null])
  );
  const invoiceIssueDatesById = new Map(
    invoices
      .filter((invoice) => invoice?.id !== null && invoice?.id !== undefined)
      .map((invoice) => [String(invoice.id), invoice.issueDate || ''])
  );
  const invoiceIssueDatesByNumber = new Map(
    invoices
      .filter((invoice) => invoice?.invoiceNumber)
      .map((invoice) => [invoice.invoiceNumber, invoice.issueDate || ''])
  );
  const invoiceClientIdsByNumber = new Map(
    invoices
      .filter((invoice) => invoice?.invoiceNumber)
      .map((invoice) => [invoice.invoiceNumber, invoice.clientId ?? null])
  );

  return payments.map((payment) => {
    const clientIdFromInvoiceId =
      payment.invoiceId !== null && payment.invoiceId !== undefined
        ? invoiceClientIdsById.get(String(payment.invoiceId))
        : undefined;
    const clientIdFromInvoiceNumber =
      (clientIdFromInvoiceId === undefined || clientIdFromInvoiceId === null) && payment.invoiceNumber
        ? invoiceClientIdsByNumber.get(payment.invoiceNumber)
        : undefined;

    return {
      ...payment,
      completedDealId: payment.completedDealId ?? invoicesById.get(String(payment.invoiceId))?.completedDealId ?? invoicesByNumber.get(payment.invoiceNumber)?.completedDealId ?? null,
      clientId:
        payment.clientId ??
        clientIdFromInvoiceId ??
        clientIdFromInvoiceNumber ??
        null,
      issueDate:
        payment.issueDate ||
        (payment.invoiceId !== null && payment.invoiceId !== undefined
          ? invoiceIssueDatesById.get(String(payment.invoiceId))
          : '') ||
        (payment.invoiceNumber ? invoiceIssueDatesByNumber.get(payment.invoiceNumber) : '') ||
        '',
    };
  });
}

function mapCreateInvoiceInput(form, installments) {
  return {
    ...entitySelectionPayload(form),
    invoiceNumber: (form.invoiceNumber || '').trim(),
    issueDate: form.issueDate,
    client: (form.client || '').trim(),
    ...(form.clientId !== null && form.clientId !== undefined
      ? { clientId: form.clientId }
      : {}),
    assignment: (form.assignment || '').trim(),
    ...(form.completedDealId !== null && form.completedDealId !== undefined
      ? { completedDealId: form.completedDealId }
      : {}),
    ...(form.initialPaymentStatus ? { initialPaymentStatus: form.initialPaymentStatus } : {}),
    paymentExpectedDate: form.paymentExpectedDate || null,
    ...(form.completedDealId !== null && form.completedDealId !== undefined
      ? {
          installmentCount: Number(form.installmentCount) || 1,
          installmentDueDates: Array.isArray(form.installmentDueDates)
            ? form.installmentDueDates.filter(Boolean)
            : [],
        }
      : {}),
    type: form.type,
    totalAmount: parseEuroToCents(form.totalAmount) / 100,
    notes: (form.notes || '').trim(),
    installments:
      form.type === 'rateizzato'
        ? installments
            .map((item) => ({
              amount: parseEuroToCents(item.amount) / 100,
              dueDate: item.dueDate,
            }))
        : [],
    ...(Object.prototype.hasOwnProperty.call(form, 'customFields') ? { customFields: form.customFields } : {}),
  };
}

async function performListPaymentsRequest() {
  const response = await fetch(getAccountingApiUrl(PAYMENTS_API_PATH), {
    headers: getAccountingAuthHeaders(),
  });

  if (await isModuleDisabledResponse(response)) {
    throw new Error('ACCOUNTING_MODULE_DISABLED');
  }

  const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
  if (tenantDatabaseFailure) {
    const error = new Error(tenantDatabaseFailure.error);
    error.message = tenantDatabaseFailure.error;
    error.clientMessage = tenantDatabaseFailure.message;
    throw error;
  }

  if (!response.ok) {
    throw new Error(`ACCOUNTING_PAYMENTS_LIST_FAILED_${response.status}`);
  }

  const payload = await response.json();
  if (!Array.isArray(payload)) {
    throw new Error('ACCOUNTING_PAYMENTS_LIST_INVALID_PAYLOAD');
  }

  return payload.map(mapApiPaymentToPayment);
}

async function performListInvoicesRequest() {
  const response = await fetch(getAccountingApiUrl(INVOICES_API_PATH), {
    headers: getAccountingAuthHeaders(),
  });

  if (await isModuleDisabledResponse(response)) {
    throw new Error('ACCOUNTING_MODULE_DISABLED');
  }

  const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
  if (tenantDatabaseFailure) {
    const error = new Error(tenantDatabaseFailure.error);
    error.message = tenantDatabaseFailure.error;
    error.clientMessage = tenantDatabaseFailure.message;
    throw error;
  }

  if (!response.ok) {
    throw new Error(`ACCOUNTING_INVOICES_LIST_FAILED_${response.status}`);
  }

  const payload = await response.json();
  if (!Array.isArray(payload)) {
    throw new Error('ACCOUNTING_INVOICES_LIST_INVALID_PAYLOAD');
  }

  return payload.map(mapApiInvoiceToInvoice);
}

export async function createInvoice(form, installments) {
  if (!form.issueDate || !form.clientId || !form.assignment || !form.type || !form.totalAmount) {
    return buildMutationResult({
      ok: false,
      error: 'INVOICE_VALIDATION_ERROR',
      message: 'Seleziona un cliente reale dal catalogo e compila tutti i campi obbligatori.',
    });
  }

  const totalCents = parseEuroToCents(form.totalAmount);
  if (!Number.isSafeInteger(totalCents) || totalCents <= 0) {
    return buildMutationResult({ ok: false, error: 'INVOICE_VALIDATION_ERROR', message: 'Inserisci un totale valido maggiore di zero.' });
  }
  if (form.type === 'rateizzato') {
    const amounts = installments.map((item) => parseEuroToCents(item.amount));
    if (!installments.length || installments.some((item) => !item.dueDate) || amounts.some((amount) => !Number.isSafeInteger(amount) || amount <= 0)) {
      return buildMutationResult({ ok: false, error: 'INVOICE_VALIDATION_ERROR', message: 'Inserisci importo maggiore di zero e scadenza per ogni rata.' });
    }
    if (amounts.reduce((sum, amount) => sum + amount, 0) !== totalCents) {
      return buildMutationResult({ ok: false, error: 'INVOICE_VALIDATION_ERROR', message: 'La somma delle rate deve coincidere con il totale della fattura.' });
    }
  }

  try {
    const response = await fetch(getAccountingApiUrl(INVOICES_API_PATH), {
      method: 'POST',
      headers: getAccountingAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(mapCreateInvoiceInput(form, installments)),
    });

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'INVOICE_VALIDATION_ERROR',
        message: 'Dati fattura non validi. Verifica importo totale e rate.',
      });
    }

    if (response.status === 409) {
      const payload = await readErrorPayload(response);
      return buildMutationResult({
        ok: false,
        error: 'INVOICE_ALREADY_GENERATED_FOR_COMPLETED_DEAL',
        message: payload?.message || 'Fattura già generata per questo affare',
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_MODULE_DISABLED',
        message: 'Modulo accounting non abilitato per questo workspace.',
      });
    }

    const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
    if (tenantDatabaseFailure) {
      return buildMutationResult({
        ok: false,
        error: tenantDatabaseFailure.error,
        message: tenantDatabaseFailure.message,
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend accounting.',
      });
    }

    if (!response.ok) {
      throw new Error(`ACCOUNTING_INVOICE_CREATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    const invoice = mapApiInvoiceToInvoice(payload?.invoice);
    const payments = Array.isArray(payload?.payments)
      ? attachClientIdsToPayments(payload.payments.map(mapApiPaymentToPayment), [invoice])
      : [];

    return buildMutationResult({
      ok: true,
      data: {
        invoice,
        payments,
      },
      message: 'Fattura creata con successo!',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'ACCOUNTING_API_UNAVAILABLE',
      message: 'Backend accounting non disponibile. Creazione fattura non completata.',
    });
  }
}

export async function listExpenses() {
  try {
    return await performListExpensesRequest();
  } catch {
    return [];
  }
}

export async function listPayments() {
  try {
    return await performListPaymentsRequest();
  } catch {
    return [];
  }
}

export async function listPaymentsWithClientIdsResult() {
  try {
    const [payments, invoices] = await Promise.all([
      performListPaymentsRequest(),
      performListInvoicesRequest(),
    ]);

    return {
      ok: true,
      data: attachClientIdsToPayments(payments, invoices),
      error: null,
    };
  } catch (error) {
    return {
      ok: false,
      data: [],
      error:
        error instanceof Error && error.message === 'ACCOUNTING_MODULE_DISABLED'
          ? 'ACCOUNTING_MODULE_DISABLED'
          : error instanceof Error &&
              [
                'ACCOUNTING_TENANT_DATABASE_NOT_READY',
                'ACCOUNTING_TENANT_DATABASE_UNAVAILABLE',
                'ACCOUNTING_TENANT_DATABASE_METADATA_MISSING',
              ].includes(error.message)
            ? error.message
          : error instanceof Error &&
            (/ACCOUNTING_PAYMENTS_LIST_FAILED_(401|403)$/.test(error.message) ||
              /ACCOUNTING_INVOICES_LIST_FAILED_(401|403)$/.test(error.message))
            ? 'ACCOUNTING_AUTH_REQUIRED'
            : error instanceof Error
              ? error.message
              : 'ACCOUNTING_PAYMENTS_API_UNAVAILABLE',
      message:
        error instanceof Error && error.message === 'ACCOUNTING_MODULE_DISABLED'
          ? 'Modulo accounting non abilitato per questo workspace.'
          : error instanceof Error && error.clientMessage
            ? error.clientMessage
          : '',
    };
  }
}

export async function listInvoices() {
  try {
    return await performListInvoicesRequest();
  } catch {
    return [];
  }
}

export async function listPaymentsResult() {
  try {
    const payments = await performListPaymentsRequest();

    return {
      ok: true,
      data: payments,
      error: null,
    };
  } catch (error) {
    return {
      ok: false,
      data: [],
      error:
        error instanceof Error && error.message === 'ACCOUNTING_MODULE_DISABLED'
          ? 'ACCOUNTING_MODULE_DISABLED'
          : error instanceof Error &&
              [
                'ACCOUNTING_TENANT_DATABASE_NOT_READY',
                'ACCOUNTING_TENANT_DATABASE_UNAVAILABLE',
                'ACCOUNTING_TENANT_DATABASE_METADATA_MISSING',
              ].includes(error.message)
            ? error.message
          : error instanceof Error && /ACCOUNTING_PAYMENTS_LIST_FAILED_(401|403)$/.test(error.message)
            ? 'ACCOUNTING_AUTH_REQUIRED'
            : error instanceof Error
              ? error.message
              : 'ACCOUNTING_PAYMENTS_API_UNAVAILABLE',
      message:
        error instanceof Error && error.message === 'ACCOUNTING_MODULE_DISABLED'
          ? 'Modulo accounting non abilitato per questo workspace.'
          : error instanceof Error && error.clientMessage
            ? error.clientMessage
          : '',
    };
  }
}

export async function listExpensesResult() {
  try {
    const expenses = await performListExpensesRequest();

    return {
      ok: true,
      data: expenses,
      error: null,
    };
  } catch (error) {
    return {
      ok: false,
      data: [],
      error:
        error instanceof Error && error.message === 'ACCOUNTING_MODULE_DISABLED'
          ? 'ACCOUNTING_MODULE_DISABLED'
          : error instanceof Error &&
              [
                'ACCOUNTING_TENANT_DATABASE_NOT_READY',
                'ACCOUNTING_TENANT_DATABASE_UNAVAILABLE',
                'ACCOUNTING_TENANT_DATABASE_METADATA_MISSING',
              ].includes(error.message)
            ? error.message
          : error instanceof Error && /ACCOUNTING_EXPENSES_LIST_FAILED_(401|403)$/.test(error.message)
            ? 'ACCOUNTING_AUTH_REQUIRED'
            : error instanceof Error
              ? error.message
              : 'ACCOUNTING_EXPENSES_API_UNAVAILABLE',
      message:
        error instanceof Error && error.message === 'ACCOUNTING_MODULE_DISABLED'
          ? 'Modulo accounting non abilitato per questo workspace.'
          : error instanceof Error && error.clientMessage
            ? error.clientMessage
          : '',
    };
  }
}

export async function listInvoicesResult() {
  try {
    const invoices = await performListInvoicesRequest();

    return {
      ok: true,
      data: invoices,
      error: null,
    };
  } catch (error) {
    return {
      ok: false,
      data: [],
      error:
        error instanceof Error && error.message === 'ACCOUNTING_MODULE_DISABLED'
          ? 'ACCOUNTING_MODULE_DISABLED'
          : error instanceof Error &&
              [
                'ACCOUNTING_TENANT_DATABASE_NOT_READY',
                'ACCOUNTING_TENANT_DATABASE_UNAVAILABLE',
                'ACCOUNTING_TENANT_DATABASE_METADATA_MISSING',
              ].includes(error.message)
            ? error.message
          : error instanceof Error && /ACCOUNTING_INVOICES_LIST_FAILED_(401|403)$/.test(error.message)
            ? 'ACCOUNTING_AUTH_REQUIRED'
            : error instanceof Error
              ? error.message
              : 'ACCOUNTING_INVOICES_API_UNAVAILABLE',
      message:
        error instanceof Error && error.message === 'ACCOUNTING_MODULE_DISABLED'
          ? 'Modulo accounting non abilitato per questo workspace.'
          : error instanceof Error && error.clientMessage
            ? error.clientMessage
          : '',
    };
  }
}

export async function createExpense(form, idempotencyKey = '') {
  if (form.recurring !== 'una_tantum' && form.recurrenceCount != null && form.recurrenceCount !== '' &&
      (!Number.isInteger(Number(form.recurrenceCount)) || Number(form.recurrenceCount) < 1 || Number(form.recurrenceCount) > 1000)) {
    return buildMutationResult({ ok: false, error: 'EXPENSE_VALIDATION_ERROR', message: 'Inserisci un numero di rate intero tra 1 e 1000.' });
  }

  const parsedAmount = parseNumericDraft(form.amount, { min: 0.01 });
  if (!form.description || !form.amount) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_VALIDATION_ERROR',
      message: 'Compila tutti i campi obbligatori',
    });
  }

  if (!parsedAmount.ok) {
    return buildMutationResult({ ok: false, error: 'EXPENSE_VALIDATION_ERROR', message: 'Inserisci un importo valido maggiore di zero.' });
  }

  if (form.recipientType === 'STAFF' && !form.staffId) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_VALIDATION_ERROR',
      message: 'Seleziona un membro staff per questa uscita.',
    });
  }

  if (form.recipientType === 'SUPPLIER' && !form.supplierId) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_VALIDATION_ERROR',
      message: 'Seleziona un fornitore reale dal catalogo.',
    });
  }

  if (form.recipientType === 'SUPPLIER' && !String(form.supplierName || '').trim()) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_VALIDATION_ERROR',
      message: 'Snapshot fornitore mancante.',
    });
  }

  try {
    const response = await fetch(getAccountingApiUrl(EXPENSES_API_PATH), {
      method: 'POST',
      headers: getAccountingAuthHeaders({
        'Content-Type': 'application/json',
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      }),
      body: JSON.stringify({
        description: form.description.trim(),
        type: form.type,
        amount: parsedAmount.value,
        dueDate: form.dueDate,
        ...entitySelectionPayload(form),
        ...(form.recurrenceCount !== undefined ? { recurrenceCount: form.recurring === 'una_tantum' || form.recurrenceCount === '' ? null : Number(form.recurrenceCount) } : {}),
        recurring: form.recurring,
        recipientType: form.recipientType || 'OTHER',
        staffId: form.recipientType === 'STAFF' ? Number(form.staffId) : null,
        supplierId: form.recipientType === 'SUPPLIER' ? Number(form.supplierId) : null,
        supplierName: form.recipientType === 'SUPPLIER' ? String(form.supplierName || '').trim() : null,
        notes: (form.notes || '').trim(),
        ...(Object.prototype.hasOwnProperty.call(form, 'customFields') ? { customFields: form.customFields } : {}),
      }),
    });

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_MODULE_DISABLED',
        message: 'Modulo accounting non abilitato per questo workspace.',
      });
    }

    const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
    if (tenantDatabaseFailure) {
      return buildMutationResult({
        ok: false,
        error: tenantDatabaseFailure.error,
        message: tenantDatabaseFailure.message,
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend accounting.',
      });
    }

    if (!response.ok) {
      throw new Error(`ACCOUNTING_EXPENSE_CREATE_FAILED_${response.status}`);
    }

    const payload = await response.json();

    return buildMutationResult({
      ok: true,
      data: {
        expense: mapApiExpenseToExpense(payload),
      },
      message: 'Uscita creata con successo!',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'ACCOUNTING_API_UNAVAILABLE',
      message: 'Backend accounting non disponibile. Creazione uscita non completata.',
    });
  }
}

export async function updateExpense(expenseId, form) {
  if (form.recurring !== 'una_tantum' && form.recurrenceCount != null && form.recurrenceCount !== '' &&
      (!Number.isInteger(Number(form.recurrenceCount)) || Number(form.recurrenceCount) < 1 || Number(form.recurrenceCount) > 1000)) {
    return buildMutationResult({ ok: false, error: 'EXPENSE_VALIDATION_ERROR', message: 'Inserisci un numero di rate intero tra 1 e 1000.' });
  }

  if (!expenseId) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_NOT_FOUND',
      message: 'Uscita non trovata.',
    });
  }

  const parsedAmount = parseNumericDraft(form.amount, { min: 0.01 });
  if (!form.description || !form.amount) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_VALIDATION_ERROR',
      message: 'Compila tutti i campi obbligatori',
    });
  }

  if (!parsedAmount.ok) {
    return buildMutationResult({ ok: false, error: 'EXPENSE_VALIDATION_ERROR', message: 'Inserisci un importo valido maggiore di zero.' });
  }

  if (form.recipientType === 'STAFF' && !form.staffId) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_VALIDATION_ERROR',
      message: 'Seleziona un membro staff per questa uscita.',
    });
  }

  if (form.recipientType === 'SUPPLIER' && !form.supplierId) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_VALIDATION_ERROR',
      message: 'Seleziona un fornitore reale dal catalogo.',
    });
  }

  if (form.recipientType === 'SUPPLIER' && !String(form.supplierName || '').trim()) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_VALIDATION_ERROR',
      message: 'Snapshot fornitore mancante.',
    });
  }

  try {
    const response = await fetch(getAccountingApiUrl(`${EXPENSES_API_PATH}/${expenseId}`), {
      method: 'PUT',
      headers: getAccountingAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        description: form.description.trim(),
        type: form.type,
        amount: parsedAmount.value,
        dueDate: form.dueDate,
        ...entitySelectionPayload(form),
        ...(form.recurrenceCount !== undefined ? { recurrenceCount: form.recurring === 'una_tantum' || form.recurrenceCount === '' ? null : Number(form.recurrenceCount) } : {}),
        recurring: form.recurring,
        recipientType: form.recipientType || 'OTHER',
        staffId: form.recipientType === 'STAFF' ? Number(form.staffId) : null,
        supplierId: form.recipientType === 'SUPPLIER' ? Number(form.supplierId) : null,
        supplierName: form.recipientType === 'SUPPLIER' ? String(form.supplierName || '').trim() : null,
        notes: (form.notes || '').trim(),
        ...(Object.prototype.hasOwnProperty.call(form, 'customFields') ? { customFields: form.customFields } : {}),
      }),
    });

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'EXPENSE_NOT_FOUND',
        message: 'Uscita non trovata.',
      });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'EXPENSE_VALIDATION_ERROR',
        message: 'Dati uscita non validi.',
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_MODULE_DISABLED',
        message: 'Modulo accounting non abilitato per questo workspace.',
      });
    }

    const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
    if (tenantDatabaseFailure) {
      return buildMutationResult({
        ok: false,
        error: tenantDatabaseFailure.error,
        message: tenantDatabaseFailure.message,
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend accounting.',
      });
    }

    if (!response.ok) {
      throw new Error(`ACCOUNTING_EXPENSE_UPDATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        expense: mapApiExpenseToExpense(payload),
      },
      message: 'Uscita aggiornata con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'ACCOUNTING_API_UNAVAILABLE',
      message: 'Backend accounting non disponibile. Modifica uscita non completata.',
    });
  }
}

async function runAccountingMutation({ path, method, body, successMessage, mapData, forbiddenError, conflictError, conflictMessage }) {
  try {
    const response = await fetch(getAccountingApiUrl(path), {
      method,
      headers: getAccountingAuthHeaders(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_MODULE_DISABLED',
        message: 'Modulo accounting non abilitato per questo workspace.',
      });
    }

    const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
    if (tenantDatabaseFailure) {
      return buildMutationResult({
        ok: false,
        error: tenantDatabaseFailure.error,
        message: tenantDatabaseFailure.message,
      });
    }

    const capabilityDeniedPayload = await readCapabilityDeniedPayload(response);
    if (capabilityDeniedPayload?.capability === 'canManageAccountingDestructiveActions') {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_DESTRUCTIVE_CAPABILITY_DENIED',
        message:
          capabilityDeniedPayload.message ||
          'Non hai i permessi per questa operazione contabile.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend accounting.',
      });
    }

    if (response.status === 403) {
      return buildMutationResult({
        ok: false,
        error: forbiddenError,
        message: 'Non hai i permessi per questa operazione.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_RECORD_NOT_FOUND',
        message: 'Record non trovato.',
      });
    }

    if (response.status === 409) {
      return buildMutationResult({
        ok: false,
        error: conflictError,
        message: conflictMessage,
      });
    }

    if (!response.ok) {
      throw new Error(`ACCOUNTING_MUTATION_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: mapData(payload),
      message: successMessage,
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'ACCOUNTING_API_UNAVAILABLE',
      message: 'Backend accounting non disponibile. Operazione non completata.',
    });
  }
}

export async function voidInvoice(invoiceId) {
  return runAccountingMutation({
    path: `${INVOICES_API_PATH}/${invoiceId}/void`,
    method: 'POST',
    successMessage: 'Fattura annullata con successo.',
    mapData: (payload) => ({ invoice: mapApiInvoiceToInvoice(payload) }),
    forbiddenError: 'ACCOUNTING_INVOICE_VOID_FORBIDDEN',
    conflictError: 'ACCOUNTING_INVOICE_VOID_CONFLICT',
    conflictMessage: 'La fattura non puo essere annullata nello stato corrente.',
  });
}

export async function updateInvoice(invoiceId, invoice = {}) {
  const { client, clientId, assignment, issueDate, customFields } = invoice;
  return runAccountingMutation({
    path: `${INVOICES_API_PATH}/${invoiceId}`,
    method: 'PUT',
    body: { ...entitySelectionPayload(invoice), client: String(client || '').trim(), clientId: clientId || null, assignment: String(assignment || '').trim(), issueDate, ...(Object.prototype.hasOwnProperty.call(invoice, 'customFields') ? { customFields } : {}) },
    successMessage: 'Fattura aggiornata con successo.',
    mapData: (payload) => ({ invoice: mapApiInvoiceToInvoice(payload) }),
    forbiddenError: 'ACCOUNTING_INVOICE_UPDATE_FORBIDDEN',
  });
}

export async function voidExpense(expenseId) {
  return runAccountingMutation({
    path: `${EXPENSES_API_PATH}/${expenseId}/void`,
    method: 'POST',
    successMessage: 'Spesa annullata con successo.',
    mapData: (payload) => ({ expense: mapApiExpenseToExpense(payload) }),
    forbiddenError: 'ACCOUNTING_EXPENSE_VOID_FORBIDDEN',
    conflictError: 'ACCOUNTING_EXPENSE_VOID_CONFLICT',
    conflictMessage: 'La spesa non puo essere annullata nello stato corrente.',
  });
}

export async function reversePayment(paymentId) {
  return runAccountingMutation({
    path: `${PAYMENTS_API_PATH}/${paymentId}/reverse`,
    method: 'POST',
    successMessage: 'Incasso stornato con successo.',
    mapData: (payload) => ({ payment: mapApiPaymentToPayment(payload) }),
    forbiddenError: 'ACCOUNTING_PAYMENT_REVERSE_FORBIDDEN',
    conflictError: 'ACCOUNTING_PAYMENT_REVERSE_CONFLICT',
    conflictMessage: 'Solo gli incassi registrati possono essere stornati.',
  });
}

export async function restorePayment(paymentId) {
  return runAccountingMutation({
    path: `${PAYMENTS_API_PATH}/${paymentId}/restore`, method: 'POST',
    successMessage: 'Incasso ripristinato: da incassare.',
    mapData: (payload) => ({ payment: mapApiPaymentToPayment(payload) }),
    forbiddenError: 'ACCOUNTING_PAYMENT_RESTORE_FORBIDDEN', conflictError: 'ACCOUNTING_PAYMENT_RESTORE_CONFLICT',
    conflictMessage: 'Puoi ripristinare solo un incasso stornato con fattura ancora attiva.',
  });
}

export async function cancelPayment(paymentId) {
  return runAccountingMutation({
    path: `${PAYMENTS_API_PATH}/${paymentId}/cancel`, method: 'POST',
    successMessage: 'Incasso annullato con successo.',
    mapData: (payload) => ({ payment: mapApiPaymentToPayment(payload) }),
    forbiddenError: 'ACCOUNTING_PAYMENT_CANCEL_FORBIDDEN', conflictError: 'ACCOUNTING_PAYMENT_CANCEL_CONFLICT',
    conflictMessage: 'L’incasso non può essere annullato nello stato corrente.',
  });
}

export async function updatePayment(paymentId, { status, paymentDate, notes, reason, assignment, amount, dueDate, client, clientIds }) {
  if (!paymentId) {
    return buildMutationResult({
      ok: false,
      error: 'PAYMENT_NOT_FOUND',
      message: 'Incasso non trovato.',
    });
  }

  try {
    const response = await fetch(getAccountingApiUrl(`${PAYMENTS_API_PATH}/${paymentId}`), {
      method: 'PUT',
      headers: getAccountingAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        ...(assignment !== undefined ? { assignment } : {}),
        ...(reason !== undefined ? { reason: String(reason || '').trim() } : {}),
        ...(amount !== undefined ? { amount: parseEuroToCents(amount) / 100 } : {}),
        ...(dueDate !== undefined ? { dueDate } : {}),
        ...(client !== undefined ? { client } : {}),
        ...(clientIds !== undefined ? { clientIds: clientIds.map(Number) } : {}),
        status,
        paymentDate: status === 'incassato' ? paymentDate || new Date().toISOString().split('T')[0] : null,
        notes: notes || '',
      }),
    });

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'PAYMENT_NOT_FOUND',
        message: 'Incasso non trovato.',
      });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'PAYMENT_VALIDATION_ERROR',
        message: 'Stato incasso non valido.',
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_MODULE_DISABLED',
        message: 'Modulo accounting non abilitato per questo workspace.',
      });
    }

    const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
    if (tenantDatabaseFailure) {
      return buildMutationResult({
        ok: false,
        error: tenantDatabaseFailure.error,
        message: tenantDatabaseFailure.message,
      });
    }

    if (response.status === 409) {
      return buildMutationResult({ ok: false, error: 'PAYMENT_UPDATE_CONFLICT', message: 'La modifica non è consentita: verifica lo stato dell’incasso e il piano della fattura o dell’affare collegato.' });
    }
    if (response.status === 403) {
      return buildMutationResult({ ok: false, error: 'PAYMENT_UPDATE_FORBIDDEN', message: 'Non hai i permessi per questa modifica dell’incasso.' });
    }
    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend accounting.',
      });
    }

    if (!response.ok) {
      throw new Error(`ACCOUNTING_PAYMENT_UPDATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        payment: mapApiPaymentToPayment(payload),
      },
      message: 'Incasso aggiornato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'ACCOUNTING_API_UNAVAILABLE',
      message: 'Backend accounting non disponibile. Modifica incasso non completata.',
    });
  }
}

export async function registerPayment(
  paymentId,
  paymentDate = new Date().toISOString().split('T')[0]
) {
  if (!paymentId) {
    return buildMutationResult({
      ok: false,
      error: 'PAYMENT_NOT_FOUND',
      message: 'Incasso non trovato.',
    });
  }

  try {
    const response = await fetch(
      getAccountingApiUrl(`${PAYMENTS_API_PATH}/${paymentId}/register-payment`),
      {
        method: 'POST',
        headers: getAccountingAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ paymentDate }),
      }
    );

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'PAYMENT_NOT_FOUND',
        message: 'Incasso non trovato.',
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_MODULE_DISABLED',
        message: 'Modulo accounting non abilitato per questo workspace.',
      });
    }

    const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
    if (tenantDatabaseFailure) {
      return buildMutationResult({
        ok: false,
        error: tenantDatabaseFailure.error,
        message: tenantDatabaseFailure.message,
      });
    }

    if (response.status === 409) {
      return buildMutationResult({
        ok: false,
        error: 'PAYMENT_ALREADY_RECEIVED',
        message: 'Questo incasso risulta gia registrato.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend accounting.',
      });
    }

    if (!response.ok) {
      throw new Error(`ACCOUNTING_PAYMENT_REGISTER_FAILED_${response.status}`);
    }

    const payload = await response.json();

    return buildMutationResult({
      ok: true,
      data: {
        payment: mapApiPaymentToPayment(payload),
      },
      message: 'Incasso registrato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'ACCOUNTING_API_UNAVAILABLE',
      message: 'Backend accounting non disponibile. Registrazione incasso non completata.',
    });
  }
}

export async function registerExpensePayment(
  expenseId,
  paymentDate = new Date().toISOString().split('T')[0]
) {
  if (!expenseId) {
    return buildMutationResult({
      ok: false,
      error: 'EXPENSE_NOT_FOUND',
      message: 'Pagamento uscita non trovato.',
    });
  }

  try {
    const response = await fetch(
      getAccountingApiUrl(`${EXPENSES_API_PATH}/${expenseId}/register-payment`),
      {
        method: 'POST',
        headers: getAccountingAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ paymentDate }),
      }
    );

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'EXPENSE_NOT_FOUND',
        message: 'Pagamento uscita non trovato.',
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_MODULE_DISABLED',
        message: 'Modulo accounting non abilitato per questo workspace.',
      });
    }

    const tenantDatabaseFailure = await getTenantDatabaseFailure(response);
    if (tenantDatabaseFailure) {
      return buildMutationResult({
        ok: false,
        error: tenantDatabaseFailure.error,
        message: tenantDatabaseFailure.message,
      });
    }

    if (response.status === 409) {
      return buildMutationResult({
        ok: false,
        error: 'EXPENSE_ALREADY_PAID',
        message: 'Questa uscita risulta gia pagata.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'ACCOUNTING_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend accounting.',
      });
    }

    if (!response.ok) {
      throw new Error(`ACCOUNTING_EXPENSE_PAYMENT_FAILED_${response.status}`);
    }

    const payload = await response.json();

    return buildMutationResult({
      ok: true,
      data: {
        expense: mapApiExpenseToExpense(payload),
      },
      message: 'Pagamento registrato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'ACCOUNTING_API_UNAVAILABLE',
      message: 'Backend accounting non disponibile. Pagamento uscita non completato.',
    });
  }
}

export { mapApiExpenseToExpense };

function mapAccountingComment(comment) {
  return { ...comment, text: comment?.body || '' };
}

export async function listAccountingComments(entityType, entityId) {
  try {
    const response = await fetch(getAccountingApiUrl(`/api/accounting/${String(entityType).toLowerCase()}/${entityId}/comments`), {
      headers: getAccountingAuthHeaders(),
    });
    if (!response.ok) throw new Error(`ACCOUNTING_COMMENTS_LIST_FAILED_${response.status}`);
    return buildMutationResult({ ok: true, data: (await response.json()).map(mapAccountingComment) });
  } catch (error) {
    return buildMutationResult({ ok: false, error: error.message, data: [], message: 'Commenti non disponibili.' });
  }
}

export async function createAccountingComment(entityType, entityId, payload) {
  try {
    const response = await fetch(getAccountingApiUrl(`/api/accounting/${String(entityType).toLowerCase()}/${entityId}/comments`), {
      method: 'POST', headers: getAccountingAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ body: payload.body || payload.text, parentId: payload.parentId || null }),
    });
    if (!response.ok) throw new Error(`ACCOUNTING_COMMENT_CREATE_FAILED_${response.status}`);
    return buildMutationResult({ ok: true, data: mapAccountingComment(await response.json()) });
  } catch (error) {
    return buildMutationResult({ ok: false, error: error.message, message: 'Commento non salvato.' });
  }
}

export async function deleteAccountingComment(entityType, entityId, commentId) {
  try {
    const response = await fetch(getAccountingApiUrl(`/api/accounting/${String(entityType).toLowerCase()}/${entityId}/comments/${commentId}`), {
      method: 'DELETE', headers: getAccountingAuthHeaders(),
    });
    if (!response.ok) throw new Error(`ACCOUNTING_COMMENT_DELETE_FAILED_${response.status}`);
    return buildMutationResult({ ok: true });
  } catch (error) {
    return buildMutationResult({ ok: false, error: error.message, message: 'Commento non eliminato.' });
  }
}

export async function createImmediatePayment(input, idempotencyKey) {
  try {
    const response = await fetch(getAccountingApiUrl(`${PAYMENTS_API_PATH}/immediate`), {
      method: 'POST', headers: getAccountingAuthHeaders({ 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey }),
      body: JSON.stringify(input),
    });
    const errorPayload = !response.ok ? await readErrorPayload(response) : null;
    if (!response.ok) return buildMutationResult({ ok: false, error: `IMMEDIATE_PAYMENT_FAILED_${response.status}`, message: errorPayload?.message || 'Incasso immediato non creato.' });
    return buildMutationResult({ ok: true, data: { payment: mapApiPaymentToPayment(await response.json()) }, message: 'Incasso immediato registrato.' });
  } catch {
    return buildMutationResult({ ok: false, error: 'ACCOUNTING_API_UNAVAILABLE', message: 'Backend accounting non disponibile.' });
  }
}
